import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Fade } from "@mui/material";
import { Layout } from "Components/Layout/Layout";
import { Footer } from "Components/Footer/Footer";
import { CourseCard } from "Components/CourseCard/CourseCard";
import { Tabs } from "Components/Tabs/Tabs";
import { Pagination } from "Components/Pagination/Pagination";
import { useMobile } from "Hooks/useMobile";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";
import { useFavorite } from "Hooks/useFavorite";
import { InformativeText } from "Components/InformativeText/InformativeText";
import { NotebookIcon } from "Components/Icons/Documents/NotebookIcon";
import { CourseCardSkeleton } from "Components/CourseCardSkeleton/CourseCardSkeleton";
import { useNavigate } from "react-router-dom";
import { RUTAS } from "rutas";
import "./Misfavoritos.scss";

const TABS = [{ label: "Cursos" }, { label: "Paquete de Cursos" }];
const ANIMATION_DURATION = 300;

export const Misfavoritos = () => {
  const isMobile = useMobile();
  const { authUser } = useSelector((state) => state.auth);
  const { cursos, paquetes, isLoading } = useCursosPaquetes();
  const { toggleFavorite, isItemProcessing } = useFavorite(authUser.idUsuario);
  const [activeTab, setActiveTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = isMobile ? 10 : 9;
  const currentData = activeTab === 1 ? cursos : paquetes;
  const navigate = useNavigate();

  const favoriteData = useMemo(
    () => currentData.filter((item) => Boolean(item.favorito)),
    [currentData]
  );

  useEffect(() => {
    const maxPossiblePage = Math.ceil(favoriteData.length / itemsPerPage);
    if (currentPage > maxPossiblePage && maxPossiblePage > 0) {
      setCurrentPage(maxPossiblePage);
    }
  }, [favoriteData.length, itemsPerPage, currentPage]);

  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const slicedData = favoriteData.slice(startIndex, endIndex);
    const total = Math.ceil(favoriteData.length / itemsPerPage);

    return {
      paginatedData: slicedData,
      totalPages: total,
    };
  }, [favoriteData, currentPage, itemsPerPage]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handlePageChange = (e, page) => {
    setCurrentPage(page);
  };

  const handleCardClick = (learningItem) => {
    const { idCurso, idPaquete } = learningItem;

    const path = idCurso ? RUTAS.CURSO(idCurso) : RUTAS.PAQUETE(idPaquete);
    navigate(path);
  };

  const getFavoriteCountDisplay = () => {
    if (isLoading) return "00";
    return String(favoriteData.length).padStart(2, "0");
  };

  const getCurrentCountDisplay = () => {
    if (isLoading) return "00";
    return String(currentData.length).padStart(2, "0");
  };

  return (
    <Layout>
      <div className="mis-favoritos">
        <div className="mis-favoritos__tabs-wrapper">
          <Tabs items={TABS} activeTab={activeTab} onChange={handleTabChange} />
          <h1 className="mis-favoritos__title">
            Mis Favoritos: {getFavoriteCountDisplay()}/
            {getCurrentCountDisplay()}
          </h1>
        </div>

        <div className="mis-favoritos__container">
          {!isLoading && favoriteData.length === 0 && (
            <div className="mis-favoritos__empty-state">
              <InformativeText>
                <NotebookIcon />
                No tienes elementos marcados como favoritos
              </InformativeText>
            </div>
          )}

          <div className="mis-favoritos__grid">
            {!isLoading &&
              paginatedData.map((learningItem) => {
                const isProcessing = isItemProcessing(learningItem.idAlterno);
                return (
                  <Fade
                    key={learningItem.idAlterno}
                    appear={true}
                    in={true}
                    timeout={ANIMATION_DURATION}
                    unmountOnExit
                  >
                    <CourseCard
                      title={learningItem.titulo}
                      imageUrl={learningItem.img}
                      duration={learningItem.duracion}
                      difficulty={learningItem.dificultad}
                      language={learningItem.idioma}
                      badgeText={learningItem.categoria}
                      badgeTextColor={learningItem.colorTexto}
                      badgeBackgroundColor={learningItem.colorFondo}
                      badgeBorderColor={learningItem.colorBorde}
                      isFavorite={Boolean(learningItem.favorito)}
                      variant="default"
                      isLoggedIn={true}
                      onToggleFavorite={() => toggleFavorite(learningItem)}
                      onClick={() => handleCardClick(learningItem)}
                      isProcessingFavorite={isProcessing}
                    />
                  </Fade>
                );
              })}

            {isLoading &&
              Array.from({ length: itemsPerPage }).map((_, index) => (
                <CourseCardSkeleton key={index} variant="default" />
              ))}
          </div>

          {favoriteData.length > 0 && (
            <div className="mis-favoritos__pagination-container">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </Layout>
  );
};
